/**
 * TemplateDashboard.tsx
 * Dashboard Canva-like — porté depuis le bundle Snapbooth Redesign.
 * Sidebar navigation + topbar search + hero + formats + projets récents + templates.
 *
 * La logique métier (cloudManager, navigate, handlers CRUD) est préservée.
 * Seul le rendu est refondu pour matcher la maquette.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Plus,
  Folder,
  Layout as LayoutIcon,
  Star,
  Upload,
  Trash2,
  Search,
  Bell,
  HelpCircle,
  ChevronRight,
  Edit3,
  Copy,
  Pencil,
  Image as ImageIcon,
  Square as SquareIcon,
  Heart,
  ArrowUpDown,
  Eye,
  Zap,
  LogOut,
} from 'lucide-react';
import { useAppStore, PRESET_FORMATS } from '../../../services/StateService';
import { useToast } from '../common/ToastProvider';
import { useAuth } from '../../../hooks/useAuth';
import { LazySuspense } from '../common/LazySuspense';
import { UserGuide } from '../common/LazyComponents';
import { ConfirmModal } from '../common/ConfirmModal';
import { PromptModal } from '../common/PromptModal';
import { logger } from '../../../utils/ProfessionalLogger';
// FIX : le dashboard passait par cloudManager (via AppCoordinator), NULL sur la
// route /dashboard → aucune requête, « Aucun projet » + CRUD mort. On utilise
// projectService qui interroge Supabase directement (comme la vue Live), sans
// dépendre de l'initialisation de l'éditeur.
import { projectService } from '../../../services/ProjectService';
import { isSupabaseConfigured } from '../../../services/SupabaseService';
import type { ProjectData } from '../../../types/ProjectData';
import { OnboardingWizard, type OnboardingChoice } from '../Onboarding/OnboardingWizard';

/* ─── Static data : catégories du bundle ─── */
// A11/A13 : les catégories sont prévues pour la galerie publique (à venir).
// Les comptages n'existent pas encore → on les supprime pour ne pas induire
// l'utilisateur en erreur (248, 64… étaient des valeurs fictives codées en dur).
const CATEGORIES = [
  { id: 'all', label: 'Tous' },
  { id: 'wedding', label: 'Mariage' },
  { id: 'birthday', label: 'Anniversaire' },
  { id: 'corporate', label: 'Entreprise' },
  { id: 'holiday', label: 'Fêtes' },
  { id: 'minimalist', label: 'Minimaliste' },
  { id: 'vintage', label: 'Vintage' },
  { id: 'fun', label: 'Fun' },
] as const;

interface QuickFormat {
  id: string;
  name: string;
  sub: string;
  ratio: string;
  preset: string | null;
  custom?: boolean;
}
const QUICK_FORMATS: QuickFormat[] = [
  {
    id: 'p46',
    name: 'Photobooth 4 × 6',
    sub: '1200 × 1800 px · le standard',
    ratio: '4/6',
    preset: 'photobooth_10x15',
  },
  {
    id: 's26',
    name: 'Strip 2 × 6',
    sub: '600 × 1800 px · 3 photos',
    ratio: '2/6',
    preset: '2x6_strip',
  },
  {
    id: 'sq',
    name: 'Carré Insta',
    sub: '1080 × 1080 px · réseaux',
    ratio: '1/1',
    preset: 'square_1080',
  },
  {
    id: 'l64',
    name: 'Paysage 6 × 4',
    sub: '1800 × 1200 px · groupe',
    ratio: '6/4',
    preset: 'photobooth_15x10',
  },
  {
    id: 'story',
    name: 'Story 9:16',
    sub: '1080 × 1920 px · stories',
    ratio: '9/16',
    preset: 'story_9x16',
  },
  {
    id: 'custom',
    name: 'Personnalisé',
    sub: 'Dimensions au choix',
    ratio: '4/6',
    preset: null,
    custom: true,
  },
];

/* ─── Sidebar ─── */
const DashSidebar: React.FC<{
  onCreate: () => void;
  onNavigate: (path: string) => void;
  onComingSoon: () => void;
}> = ({ onCreate, onNavigate, onComingSoon }) => {
  // Items principaux. M7 : seuls Accueil et Mes projets affichent une vraie vue
  // (la liste des projets). Les autres ne sont pas encore implémentés → marqués
  // `soon` (désactivés + badge « Bientôt ») au lieu de naviguer vers la même page
  // en faisant croire à des sections distinctes.
  const items = [
    { id: 'home', label: 'Accueil', icon: Sparkles, path: '/dashboard', active: true, soon: false },
    { id: 'projects', label: 'Mes projets', icon: Folder, path: '/dashboard', soon: false },
    { id: 'templates', label: 'Templates', icon: LayoutIcon, path: '/dashboard', soon: true },
    { id: 'brand', label: 'Ma marque', icon: Star, path: '/dashboard', soon: true },
    { id: 'uploads', label: 'Uploads', icon: Upload, path: '/dashboard', soon: true },
    { id: 'trash', label: 'Corbeille', icon: Trash2, path: '/dashboard', soon: true },
  ];

  const soonBadge = (
    <span
      style={{
        marginLeft: 'auto',
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        padding: '2px 6px',
        borderRadius: 999,
        background: 'var(--bg-chip, rgba(255,255,255,0.08))',
        color: 'var(--text-tertiary, #8a8a99)',
      }}
    >
      Bientôt
    </span>
  );

  // P3 redesign : nouveaux liens vers les vues événement (Snapbooth Redesign)
  const eventItems = [
    { id: 'kiosk', label: 'Aperçu kiosque', icon: Eye, path: '/kiosk', accent: '#7C3AED' },
    { id: 'live', label: 'Live évènement', icon: Zap, path: '/live', accent: '#10B981' },
  ];

  const teams = [
    { label: 'Studio Lumière', color: '#7C3AED' },
    { label: 'Événements & Co', color: '#F472B6' },
    { label: '+ Inviter une équipe', color: 'transparent' },
  ];

  return (
    <aside className="sb-dash-sidebar" aria-label="Navigation">
      <button
        className="sb-btn sb-btn-primary"
        style={{ width: '100%', justifyContent: 'flex-start' }}
        onClick={onCreate}
      >
        <Plus />
        Créer un design
      </button>

      <nav className="sb-dash-nav" style={{ marginTop: 16 }}>
        {items.map(i => {
          const I = i.icon;
          return (
            <button
              key={i.id}
              className={`sb-dash-nav-item ${i.active ? 'is-active' : ''}`}
              onClick={() => !i.soon && onNavigate(i.path)}
              disabled={i.soon}
              aria-disabled={i.soon}
              title={i.soon ? 'Bientôt disponible' : undefined}
              style={i.soon ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
            >
              <I size={16} />
              <span>{i.label}</span>
              {i.soon && soonBadge}
            </button>
          );
        })}
      </nav>

      {/* Vues événement (Kiosque + Live) — nouveau dans le Snapbooth Redesign */}
      <div className="sb-dash-divider" />
      <div className="sb-dash-nav-section">Évènement</div>
      <nav className="sb-dash-nav">
        {eventItems.map(i => {
          const I = i.icon;
          return (
            <button key={i.id} className="sb-dash-nav-item" onClick={() => onNavigate(i.path)}>
              <I size={16} style={{ color: i.accent }} />
              <span>{i.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="sb-dash-divider" />
      <div className="sb-dash-nav-section">Espaces partagés</div>
      <nav className="sb-dash-nav">
        {teams.map((t, i) => (
          <button
            key={i}
            className="sb-dash-nav-item"
            disabled
            aria-disabled
            title="Bientôt disponible"
            style={{ opacity: 0.55, cursor: 'not-allowed' }}
          >
            <div
              className="sb-dash-team-dot"
              style={{
                background: t.color,
                border: t.color === 'transparent' ? '1.5px dashed var(--border-strong)' : 'none',
              }}
            />
            <span>{t.label}</span>
            {i === 0 && soonBadge}
          </button>
        ))}
      </nav>

      <div style={{ flex: 1 }} />

      <div className="sb-dash-storage">
        <div className="sb-dash-storage-head">
          <span style={{ fontWeight: 600 }}>Stockage</span>
          <span>4.2 / 10 Go</span>
        </div>
        <div className="sb-dash-storage-bar">
          <div style={{ width: '42%' }} />
        </div>
        <button
          className="sb-btn sb-btn-secondary sb-btn-sm"
          style={{ width: '100%', marginTop: 10 }}
          onClick={onComingSoon}
        >
          <Sparkles size={13} /> Passer en Pro
        </button>
      </div>
    </aside>
  );
};

/* ─── Topbar ─── */
const DashTopbar: React.FC<{
  search: string;
  onSearch: (v: string) => void;
  onHelp: () => void;
  searchLoading?: boolean;
}> = ({ search, onSearch, onHelp, searchLoading }) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { addToast } = useToast();
  const [menuOpen, setMenuOpen] = useState(false);

  const initials = (user?.email || 'U').slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    setMenuOpen(false);
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <header className="sb-dash-topbar">
      <div className="sb-dash-search" style={{ position: 'relative' }}>
        {searchLoading ? (
          <ArrowUpDown size={16} style={{ opacity: 0.5, animation: 'spin 1s linear infinite' }} />
        ) : (
          <Search size={16} />
        )}
        <input
          placeholder="Chercher dans vos templates, projets…"
          value={search}
          onChange={e => onSearch(e.target.value)}
        />
        {search ? (
          <button
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px 6px',
              opacity: 0.5,
              fontSize: 14,
              lineHeight: 1,
            }}
            onClick={() => onSearch('')}
            aria-label="Effacer la recherche"
          >
            ×
          </button>
        ) : (
          <kbd>⌘K</kbd>
        )}
      </div>
      <div className="sb-dash-topbar-actions">
        <button className="sb-btn sb-btn-ghost" aria-label="Aide" title="Aide" onClick={onHelp}>
          <HelpCircle size={18} />
        </button>
        <button
          className="sb-btn sb-btn-ghost"
          aria-label="Notifications"
          title="Notifications"
          onClick={() => addToast({ type: 'info', message: 'Aucune notification pour le moment.' })}
        >
          <Bell size={18} />
        </button>
        <div style={{ position: 'relative' }}>
          <button
            className="sb-dash-avatar"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Menu du compte"
            onClick={() => setMenuOpen(o => !o)}
          >
            {initials}
          </button>
          {menuOpen && (
            <>
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                onClick={() => setMenuOpen(false)}
                aria-hidden="true"
              />
              <div className="sb-mode-user-menu" role="menu">
                {user?.email && <div className="sb-mode-user-menu-email">{user.email}</div>}
                <button
                  type="button"
                  role="menuitem"
                  className="sb-mode-user-menu-item"
                  onClick={handleLogout}
                >
                  <LogOut size={14} />
                  <span>Se déconnecter</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

/* ─── Hero ─── */
const Hero: React.FC<{ onCreate: () => void; onComingSoon: () => void }> = ({
  onCreate,
  onComingSoon,
}) => {
  return (
    <section className="sb-dash-hero">
      <div>
        <div className="sb-dash-chip">
          <Sparkles size={12} /> Nouveau · IA assistant
        </div>
        <h1 className="sb-dash-h1">Créez votre template photobooth en moins de 3 minutes.</h1>
        <p className="sb-dash-hero-sub">
          Partez d'un modèle, glissez votre logo, ajustez le texte. Vos invités repartent avec un
          souvenir qui claque.
        </p>
        <div className="sb-dash-hero-cta">
          <button className="sb-btn sb-btn-primary sb-btn-lg" onClick={onCreate}>
            <Plus size={16} /> Nouveau template
          </button>
          <button className="sb-btn sb-btn-secondary sb-btn-lg" onClick={onComingSoon}>
            <Sparkles size={16} /> Démarrer avec l'IA
          </button>
        </div>
      </div>
      <div className="sb-dash-hero-visual" aria-hidden="true">
        <div className="sb-dash-hero-stack">
          {[
            { color: '#D4AF37', tx: -22, ty: 8, rot: -8, z: 1, label: 'Sarah & Louis' },
            { color: '#EC4899', tx: 0, ty: 0, rot: 0, z: 2, label: 'Anniversaire' },
            { color: '#10B981', tx: 22, ty: 12, rot: 8, z: 1, label: 'Soirée Neon' },
          ].map((t, i) => (
            <div
              key={i}
              className="sb-dash-hero-card"
              style={{
                transform: `translate(${t.tx}px, ${t.ty}px) rotate(${t.rot}deg)`,
                zIndex: t.z,
                background: `linear-gradient(160deg, ${t.color}, ${t.color}cc 50%, ${t.color}88)`,
              }}
            >
              {t.label}
            </div>
          ))}
          <div className="sb-dash-hero-sparkle s1">
            <Sparkles size={18} />
          </div>
          <div className="sb-dash-hero-sparkle s2">
            <Star size={12} />
          </div>
          <div className="sb-dash-hero-sparkle s3">
            <Heart size={14} />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── ProjectCard ─── */
interface ProjectCardProps {
  project: ProjectData;
  onEdit: (id: string) => void;
  onRename: (id: string, currentName: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
}
const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onEdit,
  onRename,
  onDuplicate,
  onDelete,
}) => {
  const formatDate = (ts: number) => {
    const diff = Date.now() - ts;
    const days = Math.floor(diff / 86_400_000);
    if (days === 0) return "Aujourd'hui";
    if (days === 1) return 'Hier';
    if (days < 7) return `Il y a ${days} jours`;
    return new Date(ts).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };
  const initial = project.name?.charAt(0).toUpperCase() ?? 'P';

  return (
    <div className="sb-dash-project-card" onClick={() => onEdit(project.id)}>
      <div
        className="sb-dash-project-thumb"
        style={
          project.thumbnail_url
            ? {
                backgroundImage: `url(${project.thumbnail_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                color: 'transparent', // masque la lettre initiale
              }
            : undefined
        }
      >
        {project.thumbnail_url ? null : initial}
        <div className="sb-dash-project-overlay">
          <button
            className="sb-btn sb-btn-primary sb-btn-sm"
            onClick={e => {
              e.stopPropagation();
              onEdit(project.id);
            }}
          >
            <Edit3 size={13} /> Modifier
          </button>
        </div>
      </div>
      <div className="sb-dash-project-meta">
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="sb-dash-project-name">{project.name || 'Sans titre'}</div>
          <div className="sb-dash-project-sub">Template · {formatDate(project.updatedAt)}</div>
        </div>
        <div style={{ display: 'flex', gap: 2 }}>
          <button
            className="sb-dash-icon-btn"
            onClick={e => {
              e.stopPropagation();
              onRename(project.id, project.name || '');
            }}
            aria-label="Renommer"
            title="Renommer"
          >
            <Pencil size={14} />
          </button>
          <button
            className="sb-dash-icon-btn"
            onClick={e => {
              e.stopPropagation();
              onDuplicate(project.id);
            }}
            aria-label="Dupliquer"
            title="Dupliquer"
          >
            <Copy size={14} />
          </button>
          <button
            className="sb-dash-icon-btn is-danger"
            onClick={e => {
              e.stopPropagation();
              onDelete(project.id);
            }}
            aria-label="Supprimer"
            title="Supprimer"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Dashboard ─── */
export const TemplateDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [items, setItems] = useState<ProjectData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<ProjectData[] | null>(null);
  // A11/A13 : les boutons de catégorie sont désactivés (galerie à venir) —
  // le setter ne sera réactivé que quand la galerie sera câblée.
  const [activeCat, _setActiveCat] = useState<string>('all');
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  // A04 : cible de suppression (ouvre la ConfirmModal au lieu de window.confirm).
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  // A17 : cible de renommage (ouvre la PromptModal au lieu de window.prompt).
  const [renameTarget, setRenameTarget] = useState<{ id: string; name: string } | null>(null);
  // A12 : tri des projets récents (bouton « Récents »/« A→Z »).
  const [sortMode, setSortMode] = useState<'recent' | 'name'>('recent');
  // A10 : « Tout voir » / « Réduire » (pagination simple à 8 par défaut).
  const [showAll, setShowAll] = useState(false);

  // M5 : le guide d'aide se ferme via l'événement 'close-user-guide' (UserGuide
  // n'a pas de prop onClose — il dispatche cet événement).
  useEffect(() => {
    const close = () => setShowHelp(false);
    window.addEventListener('close-user-guide', close);
    return () => window.removeEventListener('close-user-guide', close);
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await projectService.getAllProjects();
      if (error) {
        logger.error('Erreur chargement dashboard:', error);
        addToast({ type: 'error', message: 'Erreur lors du chargement des projets' });
        return;
      }
      // Mapper Project (Supabase) → ProjectData (vue dashboard). Seuls id/name/
      // updatedAt sont lus par les cartes ; on garde le reste pour cohérence.
      const mapped = (data ?? []).map(
        p =>
          ({
            id: p.id,
            name: p.name,
            createdAt: new Date(p.created_at).getTime(),
            updatedAt: new Date(p.updated_at).getTime(),
            thumbnail_url: p.thumbnail_url ?? undefined,
            project_type: 'template_editor',
            data: p.canvas_data,
          }) as unknown as ProjectData
      );
      setItems(mapped.sort((a, b) => b.updatedAt - a.updatedAt));
    } catch (error) {
      logger.error('Erreur chargement dashboard:', error);
      addToast({ type: 'error', message: 'Erreur lors du chargement des données' });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // A01 : créer EXPLICITEMENT le projet en base avant de naviguer, pour que
  // l'URL contienne immédiatement l'ID (plus de brouillon local ambigu perdu au
  // refresh). Fallback brouillon local clair si Supabase est indisponible.
  const handleNewTemplate = useCallback(
    async (presetId?: string | null) => {
      const store = useAppStore.getState();
      const preset =
        (presetId && PRESET_FORMATS.find(f => f.id === presetId)) ||
        PRESET_FORMATS.find(f => f.id === 'photobooth_10x15');
      if (preset) {
        store.updateFormat(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          { name: preset.name, width: preset.widthMm, height: preset.heightMm, unit: 'mm' } as any,
          300
        );
      }
      // Nouveau projet = canvas vierge + nom par défaut
      store.loadShapes([]);
      store.setDocumentName('Document sans titre');
      // A22 : repartir d'un fond par défaut (évite d'hériter de la couleur de fond
      // du projet précédemment ouvert dans la même session).
      store.setBackgroundColor('#ffffff');
      store.setProjectId(null);

      if (!isSupabaseConfigured()) {
        addToast({
          type: 'info',
          message: 'Mode local : ce brouillon ne sera pas sauvegardé dans le cloud.',
        });
        navigate('/editor');
        return;
      }

      try {
        const fresh = useAppStore.getState();
        const { data, error } = await projectService.createProject({
          name: fresh.canvas.documentName || 'Document sans titre',
          canvas_data: {
            shapes: fresh.shapes,
            canvas: fresh.canvas,
            project: fresh.project,
            selection: fresh.selection,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any,
        });
        if (error || !data) throw error || new Error('Création du projet échouée');
        useAppStore.getState().setProjectId(data.id);
        navigate(`/editor/${data.id}`);
      } catch (e) {
        logger.error('Création projet échouée:', e);
        addToast({
          type: 'warning',
          message: 'Création cloud impossible — ouverture en brouillon local.',
        });
        navigate('/editor');
      }
    },
    [navigate, addToast]
  );

  const handleEdit = useCallback((id: string) => navigate(`/editor/${id}`), [navigate]);

  const handleDuplicate = useCallback(
    async (id: string) => {
      try {
        const { error } = await projectService.duplicateProject(id);
        if (error) {
          addToast({ type: 'error', message: 'Erreur lors de la duplication' });
          return;
        }
        addToast({ type: 'success', message: 'Projet dupliqué' });
        loadData();
      } catch (error) {
        logger.error('Erreur duplication:', error);
        addToast({ type: 'error', message: 'Erreur lors de la duplication' });
      }
    },
    [addToast, loadData]
  );

  // A17 : ouvre la PromptModal (remplace window.prompt).
  const handleRename = useCallback((id: string, currentName: string) => {
    setRenameTarget({ id, name: currentName || 'Sans titre' });
  }, []);

  const confirmRename = useCallback(
    async (newNameRaw: string) => {
      if (!renameTarget) return;
      const { id, name: currentName } = renameTarget;
      const newName = newNameRaw.trim();
      if (!newName || newName === currentName) {
        setRenameTarget(null);
        return;
      }
      try {
        // Rename consistency : construire une canvas_data partielle avec le
        // nouveau nom pour garder `canvas_data.canvas.documentName` cohérent
        // avec la colonne `name`. Sans ça, le topbar de l'éditeur affiche
        // l'ancien nom jusqu'au prochain autosave complet.
        const existing = items.find(p => p.id === id);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const existingCanvas = (existing?.data as any)?.canvas;
        const canvasDataPatch =
          existingCanvas !== undefined
            ? ({
                ...(existing?.data as any),
                canvas: { ...existingCanvas, documentName: newName },
              } as any)
            : undefined;

        const { error } = await projectService.updateProject(id, {
          name: newName,
          ...(canvasDataPatch ? { canvas_data: canvasDataPatch } : {}),
        });
        if (error) {
          addToast({ type: 'error', message: 'Erreur lors du renommage' });
          return;
        }
        addToast({ type: 'success', message: 'Projet renommé' });
        setRenameTarget(null);
        loadData();
      } catch (error) {
        logger.error('Erreur renommage:', error);
        addToast({ type: 'error', message: 'Erreur lors du renommage' });
      }
    },
    [renameTarget, items, addToast, loadData]
  );

  // A04 : ouvre la modale de confirmation (remplace window.confirm).
  const handleDelete = useCallback(
    (id: string) => {
      const proj = items.find(p => p.id === id);
      setDeleteTarget({ id, name: proj?.name || 'ce projet' });
    },
    [items]
  );

  // A04 : suppression effective + undo. On capture la donnée AVANT suppression
  // pour pouvoir recréer le projet si l'utilisateur clique « Annuler ».
  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const { id, name } = deleteTarget;
    try {
      // Capture pour l'undo (best-effort : si échec, on supprime quand même).
      const snapshot = await projectService.getProjectById(id);
      const { error } = await projectService.deleteProject(id);
      if (error) {
        addToast({ type: 'error', message: 'Erreur lors de la suppression' });
        return;
      }
      setDeleteTarget(null);
      loadData();
      const restorable = snapshot.data;
      addToast({
        type: 'success',
        message: `« ${name} » supprimé`,
        duration: 7000,
        action: restorable
          ? {
              label: 'Annuler',
              action: () => {
                void (async () => {
                  try {
                    const { error: recreateError } = await projectService.createProject({
                      name: restorable.name,
                      canvas_data: restorable.canvas_data,
                      thumbnail_url: restorable.thumbnail_url ?? undefined,
                    });
                    if (recreateError) throw recreateError;
                    addToast({ type: 'success', message: 'Projet restauré' });
                    loadData();
                  } catch (e) {
                    logger.error('Échec de la restauration:', e);
                    addToast({ type: 'error', message: 'Restauration impossible' });
                  }
                })();
              },
            }
          : undefined,
      });
    } catch (error) {
      logger.error('Erreur suppression:', error);
      addToast({ type: 'error', message: 'Erreur lors de la suppression' });
    }
  }, [deleteTarget, addToast, loadData]);

  // Recherche Supabase (debounce 350 ms, min 2 caractères).
  // Retombe sur le filtrage local pour les queries courtes/vides.
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await projectService.searchProjects(q);
        const mapped = (data ?? []).map(
          p =>
            ({
              id: p.id,
              name: p.name,
              createdAt: new Date(p.created_at).getTime(),
              updatedAt: new Date(p.updated_at).getTime(),
              thumbnail_url: p.thumbnail_url ?? undefined,
              project_type: 'template_editor',
              data: p.canvas_data,
            }) as unknown as ProjectData
        );
        setSearchResults(mapped);
      } catch {
        setSearchResults(null);
      } finally {
        setSearchLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filteredProjects = useMemo(() => {
    const q = searchQuery.trim();
    if (!q) return items;
    // Résultats Supabase disponibles → prioritaires.
    if (searchResults !== null) return searchResults;
    // Fallback local (pendant le debounce ou si < 2 chars).
    const lq = q.toLowerCase();
    return items.filter(
      p =>
        p.name?.toLowerCase().includes(lq) ||
        (p.description && p.description.toLowerCase().includes(lq))
    );
  }, [items, searchQuery, searchResults]);

  // A12 : tri appliqué selon sortMode (récents par date, ou alphabétique).
  const sortedProjects = useMemo(() => {
    const arr = [...filteredProjects];
    if (sortMode === 'name') {
      arr.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr'));
    } else {
      arr.sort(
        (a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()
      );
    }
    return arr;
  }, [filteredProjects, sortMode]);

  // A10 : limite d'affichage (8) sauf si « Tout voir » est actif.
  const visibleProjects = useMemo(
    () => (showAll ? sortedProjects : sortedProjects.slice(0, 8)),
    [sortedProjects, showAll]
  );

  /** Onboarding → on applique le format choisi et on navigue vers /editor */
  const handleOnboardingFinish = useCallback(
    (choice: OnboardingChoice) => {
      // Mapping format wizard → preset format du store
      const formatPresetMap: Record<string, string> = {
        '4x6': 'photobooth_10x15',
        '2x6': 'strip_2x6',
        '6x4': 'photobooth_15x10',
        '1x1': 'square_1080',
        '9x16': 'story_9x16',
        // 'custom' → fallback default
      };
      void handleNewTemplate(formatPresetMap[choice.format] ?? null);
    },
    [handleNewTemplate]
  );

  // A23 : feedback honnête pour les fonctionnalités non encore disponibles
  // (IA, passage Pro) au lieu de boutons morts ou trompeurs.
  const notifyComingSoon = useCallback(
    (feature: string) => {
      addToast({ type: 'info', message: `${feature} — bientôt disponible.` });
    },
    [addToast]
  );

  return (
    <div className="sb-dash-shell" data-screen="dashboard">
      <DashSidebar
        onCreate={() => setOnboardingOpen(true)}
        onNavigate={path => navigate(path)}
        onComingSoon={() => notifyComingSoon('Offre Pro')}
      />
      <main className="sb-dash-main">
        <DashTopbar
          search={searchQuery}
          onSearch={setSearchQuery}
          onHelp={() => setShowHelp(true)}
          searchLoading={searchLoading}
        />
        <div className="sb-dash-content">
          {/* Hero */}
          <Hero
            onCreate={() => setOnboardingOpen(true)}
            onComingSoon={() => notifyComingSoon('Assistant IA')}
          />

          {/* Quick formats */}
          <section style={{ marginTop: 32 }}>
            <h2 className="sb-dash-h2">Commencer un nouveau projet</h2>
            <div className="sb-dash-formats">
              {QUICK_FORMATS.map(f => (
                <button
                  key={f.id}
                  className={`sb-dash-format-card ${f.custom ? 'is-custom' : ''}`}
                  onClick={() => void handleNewTemplate(f.preset)}
                >
                  <div className="sb-dash-format-preview" style={{ aspectRatio: f.ratio }}>
                    {f.custom ? (
                      <Plus size={20} />
                    ) : f.ratio === '1/1' ? (
                      <SquareIcon size={20} />
                    ) : f.ratio === '9/16' ? (
                      <ImageIcon size={20} />
                    ) : (
                      <LayoutIcon size={20} />
                    )}
                  </div>
                  <div>
                    <div className="sb-dash-format-name">{f.name}</div>
                    <div className="sb-dash-format-sub">{f.sub}</div>
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* Recent projects */}
          <section style={{ marginTop: 40 }}>
            <div className="sb-dash-section-head">
              <h2 className="sb-dash-h2">Vos projets récents</h2>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="sb-btn sb-btn-ghost sb-btn-sm"
                  onClick={() => setSortMode(m => (m === 'recent' ? 'name' : 'recent'))}
                  title="Changer le tri"
                >
                  <ArrowUpDown size={14} /> {sortMode === 'recent' ? 'Récents' : 'A → Z'}
                </button>
                {sortedProjects.length > 8 && (
                  <button
                    className="sb-btn sb-btn-ghost sb-btn-sm"
                    onClick={() => setShowAll(s => !s)}
                  >
                    {showAll ? 'Réduire' : `Tout voir (${sortedProjects.length})`}
                    <ChevronRight size={14} />
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="sb-dash-recent-grid">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="sb-dash-skeleton" style={{ aspectRatio: '4/6' }} />
                ))}
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="sb-dash-empty">
                <div className="sb-dash-empty-title">Aucun projet pour l'instant</div>
                <div>
                  Cliquez sur « Créer un design » ou choisissez un format ci-dessus pour démarrer.
                </div>
              </div>
            ) : (
              <div className="sb-dash-recent-grid">
                {visibleProjects.map(p => (
                  <ProjectCard
                    key={p.id}
                    project={p}
                    onEdit={handleEdit}
                    onRename={handleRename}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Browse templates */}
          <section style={{ marginTop: 48 }}>
            <div className="sb-dash-section-head">
              <h2 className="sb-dash-h2">Explorer les templates</h2>
            </div>
            <div className="sb-dash-cat-row">
              {/* A11/A13 : boutons désactivés (galerie non disponible) — les
                  comptages fictifs ont été retirés. Le clic sera réactivé quand
                  la galerie sera câblée à Supabase. */}
              {CATEGORIES.map(c => (
                <button
                  key={c.id}
                  className={`sb-dash-cat ${activeCat === c.id ? 'is-active' : ''}`}
                  disabled
                  title="Galerie publique à venir"
                  style={{ opacity: activeCat === c.id ? 1 : 0.55, cursor: 'not-allowed' }}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="sb-dash-empty">
              <div className="sb-dash-empty-title">Galerie publique à venir</div>
              <div>
                La bibliothèque de templates communautaires arrivera dans la prochaine itération.
              </div>
            </div>
          </section>

          <div style={{ height: 60 }} />
        </div>
      </main>

      {/* Onboarding wizard ouvert depuis "Créer un design" / hero CTA */}
      <OnboardingWizard
        open={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
        onFinish={handleOnboardingFinish}
      />

      {/* M5 : guide d'aide (bouton Aide de la topbar) */}
      {showHelp && (
        <LazySuspense>
          <UserGuide />
        </LazySuspense>
      )}

      {/* A04 : confirmation de suppression (remplace window.confirm) */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        danger
        title="Supprimer le projet ?"
        message={
          <>
            Le projet «&nbsp;<strong>{deleteTarget?.name}</strong>&nbsp;» sera supprimé.
            <br />
            Vous pourrez l'annuler juste après via la notification.
          </>
        }
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      {/* A17 : renommage de projet (remplace window.prompt) */}
      <PromptModal
        isOpen={!!renameTarget}
        title="Renommer le projet"
        label="Nouveau nom"
        initialValue={renameTarget?.name ?? ''}
        placeholder="Nom du projet"
        confirmLabel="Renommer"
        onConfirm={confirmRename}
        onClose={() => setRenameTarget(null)}
      />
    </div>
  );
};
